/** Lab Volume Percent New — stake / percent recalculation helpers. */

export type VolumeRateCell = {
  myShare: number
  percent: number
  apiPercent: number
  oldPercent: number
  minStake: number
  apiMinStake: number
  oldMinStake: number
  companyHarjeet: number
  oldcompanyHarjeet: number
  amountCutInBF: number
  bfHarjeet: number
  /** Lab legacy fields (Volume Percent old / Mongo dump). */
  validateStack?: number
  minBet?: number
  minRate?: number
}

export function isSpecialEvent(eventName: string): boolean {
  const n = eventName.toLowerCase()
  return (
    n.includes('line session') ||
    n.includes('horse racing') ||
    n.includes('greyhound racing')
  )
}

const MIN_STAKE_BASE_POINTS = 5
const MIN_STAKE_RATIOS_POINTS = [1, 2, 4, 10, 20, 100, 200]
const MIN_STAKE_BASE_INR = 1000
const MIN_STAKE_RATIOS_INR = [1, 2, 5, 10, 100]

export function roundUpToScaledSteps(
  value: number,
  baseUnit: number,
  ratios: number[],
): number {
  let unit = baseUnit
  for (let scale = 0; scale < 20; scale++) {
    for (let i = 0; i < ratios.length; i++) {
      const step = ratios[i]! * unit
      if (step >= value) return step
    }
    unit *= 10
  }
  return value
}

export function roundMinStake(value: number, isINR: boolean): number {
  const num = Number(value || 0)
  if (num <= 0) return 0
  if (isINR) {
    return roundUpToScaledSteps(num, MIN_STAKE_BASE_INR, MIN_STAKE_RATIOS_INR)
  }
  const high =
    MIN_STAKE_BASE_POINTS * MIN_STAKE_RATIOS_POINTS[MIN_STAKE_RATIOS_POINTS.length - 1]!
  if (num <= high) {
    return roundUpToScaledSteps(num, MIN_STAKE_BASE_POINTS, MIN_STAKE_RATIOS_POINTS)
  }
  return roundUpToScaledSteps(num, MIN_STAKE_BASE_INR, MIN_STAKE_RATIOS_INR)
}

export function calcMinStakeFromPercent(percent: number, isINR: boolean): number {
  if (percent <= 0) return 0
  const raw = isINR ? Math.ceil((1000 / percent) * 100) : Math.ceil(1000 / percent)
  return roundMinStake(raw, isINR)
}

/** Prefer Lab NEW minStake, then apiMinStake, then old minBet (skip zeros). */
export function resolveMinStake(row: VolumeRateCell): number {
  for (const v of [row.minStake, row.apiMinStake, row.minBet, row.validateStack]) {
    const n = Number(v)
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

/**
 * Lab Sportsbook PlaceBet / PlaceBetLineSession read `minBet` (and OLD
 * `validateStack`). Volume Percent New UI edits `minStake` and auto-derives it
 * for HR / GHR / Line Session from percent so a POINTS min stake (e.g. 20 at
 * 50%) is eligible for Betfair. Keep all three fields in lockstep on save.
 */
export function syncPlaceBetMinFields(row: VolumeRateCell): VolumeRateCell {
  const min = resolveMinStake(row)
  return {
    ...row,
    minStake: min,
    minBet: min,
    validateStack: min,
  }
}

/**
 * Lab OLD volume-percent.js — when percent changes for INR/Points:
 * minBet = (2 * 10000) / percent  (INR) or (2 * 100) / percent (Points).
 */
export function calcLegacyMinBet(percent: number, isINR: boolean): number {
  if (percent <= 0) return 0
  return isINR ? (2 * 10000) / percent : (2 * 100) / percent
}

export function syncMinStake(
  row: VolumeRateCell,
  eventName: string,
  currencyType: string,
): VolumeRateCell {
  const isINR = currencyType === 'INR'
  const next = { ...row }
  // Lab NEW syncMinStake: special → derive from percent; else only round if > 0.
  if (isSpecialEvent(eventName) && Number(next.percent || 0) > 0) {
    next.minStake = calcMinStakeFromPercent(next.percent, isINR)
    return syncPlaceBetMinFields(next)
  }
  const min = resolveMinStake(next)
  if (min > 0) {
    next.minStake = roundMinStake(min, isINR)
  } else {
    next.minStake = 0
  }
  return syncPlaceBetMinFields(next)
}

export type RecalcSource = 'company' | 'percent' | 'companyHarjeet' | 'manualSave' | 'default'

/**
 * Lab updateValueAction / recalc use cell myShare only.
 * Optional tmMyShare is applied only when the operator is editing company rate
 * and the cell is still 0 (CM hierarchy shareBp convenience).
 */
function companyRateOf(row: VolumeRateCell, tmMyShare?: number): number {
  const cell = Number(row.myShare || 0)
  if (cell > 0) return cell
  return Number(tmMyShare || 0)
}

/** Lab `$scope.recalcFromMinStake(eventName, tm, source)`. */
export function recalcFromMinStake(
  row: VolumeRateCell,
  eventName: string,
  currencyType: string,
  activeRate: number,
  source: RecalcSource,
  tmMyShare?: number,
): VolumeRateCell {
  const next = { ...row }
  // Only fill myShare from TM when the operator is driving a company-rate edit.
  const companyRate =
    source === 'company' || source === 'companyHarjeet'
      ? companyRateOf(next, tmMyShare)
      : Number(next.myShare || 0)
  if (
    (source === 'company' || source === 'companyHarjeet') &&
    companyRate > 0 &&
    !(Number(next.myShare) > 0)
  ) {
    next.myShare = companyRate
  }
  const special = isSpecialEvent(eventName)
  const isINR = currencyType === 'INR'
  const rate = Number(activeRate || 0)

  const applyMin = () => {
    if (special && Number(next.percent || 0) > 0) {
      next.minStake = calcMinStakeFromPercent(next.percent, isINR)
      return
    }
    // Lab NEW: non-special — keep existing min; allow 0 (do not seed OLD minBet).
    const resolved = resolveMinStake(next)
    next.minStake = resolved > 0 ? roundMinStake(resolved, isINR) : 0
  }

  /* ================= COMPANY RATE CHANGE ================= */
  if (source === 'company') {
    if (companyRate <= 0 || rate <= 0) return next

    const prevCompanyHarjeet = Number(next.companyHarjeet || 0)
    const targetBF = 100 - prevCompanyHarjeet
    const targetAmountCut = isINR ? targetBF * companyRate : (targetBF / 100) * companyRate
    next.percent = Math.ceil((targetAmountCut * 100) / rate)
    next.amountCutInBF = rate > 0 ? +((rate * next.percent) / 100).toFixed(2) : 0
    if (companyRate > 0) {
      next.bfHarjeet = isINR
        ? Math.round(next.amountCutInBF / companyRate)
        : Math.round((next.amountCutInBF / companyRate) * 100)
    }
    applyMin()
    return next
  }

  /* ================= COMPANY HARJEET CHANGE ================= */
  if (source === 'companyHarjeet') {
    if (companyRate <= 0 || rate <= 0) return next

    const prevCompanyHarjeet = Number(next.oldcompanyHarjeet ?? next.companyHarjeet ?? 0)
    const newCompanyHarjeet = Number(next.companyHarjeet || 0)
    const targetBF = 100 - newCompanyHarjeet
    const targetAmountCut = isINR ? targetBF * companyRate : (targetBF / 100) * companyRate
    next.percent = Math.ceil((targetAmountCut * 100) / rate)
    next.amountCutInBF = rate > 0 ? +((rate * next.percent) / 100).toFixed(2) : 0
    if (companyRate > 0) {
      next.bfHarjeet = isINR
        ? Math.round(next.amountCutInBF / companyRate)
        : Math.round((next.amountCutInBF / companyRate) * 100)
    }
    if (newCompanyHarjeet !== prevCompanyHarjeet) {
      next.oldcompanyHarjeet = prevCompanyHarjeet
      next.companyHarjeet = newCompanyHarjeet
    }
    applyMin()
    return next
  }

  /* ================= PERCENT CHANGE ================= */
  if (source === 'percent') {
    next.percent = Number(next.percent || 0)
    next.amountCutInBF = rate > 0 ? +((rate * next.percent) / 100).toFixed(2) : 0
    if (companyRate > 0) {
      next.bfHarjeet = isINR
        ? Math.round(next.amountCutInBF / companyRate)
        : Math.round((next.amountCutInBF / companyRate) * 100)
    }
    const prev = Number(next.companyHarjeet || 0)
    const neu = +(100 - Number(next.bfHarjeet || 0)).toFixed(2)
    if (neu !== prev) {
      next.oldcompanyHarjeet = prev
      next.companyHarjeet = neu
    }
    applyMin()
    return next
  }

  /* ================= DEFAULT / manualSave ================= */
  next.amountCutInBF = rate > 0 ? +((rate * Number(next.percent || 0)) / 100).toFixed(2) : 0
  if (companyRate > 0) {
    next.bfHarjeet = isINR
      ? Math.round(next.amountCutInBF / companyRate)
      : Math.round((next.amountCutInBF / companyRate) * 100)
  }
  const prev = Number(next.companyHarjeet || 0)
  const neu = +(100 - Number(next.bfHarjeet || 0)).toFixed(2)
  if (neu !== prev) {
    next.oldcompanyHarjeet = prev
    next.companyHarjeet = neu
  }
  applyMin()
  return next
}

type TmLike = {
  currencyType: string
  myShare?: number
  betfairRates: Record<string, VolumeRateCell>
}

/**
 * Lab: one Company Rate per TM (`tm.myShare`), copied onto every sport cell on
 * hydrate. When the operator edits Company Rate on a row, lift it to the TM and
 * sync siblings so Save persists `tm_volume_rates.my_share` (not only the cell).
 */
export function applyCompanyRateToTm<T extends TmLike>(
  tm: T,
  eventNames: string[],
  companyRate: number,
  activeRate: number,
  focusEvent?: string,
): T {
  const rate = Number(companyRate)
  const rates = { ...tm.betfairRates }
  for (const name of eventNames) {
    const row = rates[name]
    if (!row) continue
    const withShare = { ...row, myShare: rate }
    rates[name] = recalcFromMinStake(
      withShare,
      name,
      tm.currencyType,
      activeRate,
      name === focusEvent ? 'company' : 'manualSave',
      rate,
    )
  }
  return { ...tm, myShare: rate, betfairRates: rates }
}

/**
 * Hierarchy Sportsbook share bp → Lab Volume % Company Rate.
 *
 * Lab NEW percent math:
 *   INR: companyRate is memberRatePercent (0–1) → shareBp/10000
 *   POINTS / POINT@1: companyRate is share % → shareBp/100
 */
export function companyRateFromShareBp(
  shareBp: unknown,
  currencyType: string = 'INR',
): number {
  const bp = Number(shareBp ?? 0)
  if (!Number.isFinite(bp) || bp < 0) return 0
  const ccy = String(currencyType ?? '')
    .trim()
    .toUpperCase()
  if (ccy === 'POINTS' || ccy === 'POINT' || ccy === 'POINT@1' || ccy === 'POINT1') {
    return Number((bp / 100).toFixed(6))
  }
  return Number((bp / 10_000).toFixed(6))
}

/**
 * Prefer unanimous cell company rates (operator edits), else TM myShare.
 * Fixes Save writing stale tm.myShare=80 while cells already hold 0.2.
 */
export function resolveTmCompanyRateForSave(
  tmMyShare: number | undefined,
  rates: Record<string, VolumeRateCell>,
): number {
  const cellShares = Object.values(rates)
    .map((r) => Number(r.myShare))
    .filter((n) => Number.isFinite(n) && n > 0)
  if (cellShares.length > 0) {
    const rounded = cellShares.map((n) => Number(n.toFixed(6)))
    const unique = [...new Set(rounded)]
    if (unique.length === 1) return unique[0]!
  }
  const tm = Number(tmMyShare ?? 0)
  return Number.isFinite(tm) ? tm : 0
}

/**
 * Lab load hydrate — copy TM myShare onto each cell, resolve minStake from
 * Lab NEW/legacy fields, then sync special-event mins.
 */
export function hydrateTmRates<T extends TmLike>(tms: T[], eventNames: string[]): T[] {
  return tms.map((tm) => {
    const rates = { ...tm.betfairRates }
    for (const name of eventNames) {
      const row = rates[name]
      if (!row) continue
      const rawPercent = Number(
        row.percent !== undefined ? row.percent : Number(row.apiPercent || 0),
      )
      const plainPercent = normalizePlainVolumePercent(rawPercent, tm.currencyType)
      const next: VolumeRateCell = {
        ...row,
        // Lab: row.myShare = Number(tm.myShare || 0)
        myShare: Number(tm.myShare || 0),
        percent: plainPercent,
        apiPercent: normalizePlainVolumePercent(
          Number(row.apiPercent || 0),
          tm.currencyType,
        ),
        minStake: resolveMinStake(row),
        apiMinStake: Number(row.apiMinStake || 0),
      }
      rates[name] = syncMinStake(next, name, tm.currencyType)
    }
    return { ...tm, betfairRates: rates }
  })
}

/**
 * Lab `$scope.updateValueAction(value)` — recalculate every TM/event from
 * active Betfair rate while preserving companyHarjeet.
 * Uses cell myShare only (Lab). When myShare is 0 (legit INR TMs), no-op so
 * stored percent / minStake stay intact.
 */
export function applyActiveRateToTms<T extends TmLike>(
  tms: T[],
  eventNames: string[],
  activeRate: number,
): T[] {
  const rate = Number(activeRate || 0)
  return tms.map((tm) => {
    const rates = { ...tm.betfairRates }
    for (const name of eventNames) {
      if (String(name).toLowerCase() === 'artemis') continue
      const row = rates[name]
      if (!row) continue
      const oldCompanyHarjeet = Number(row.companyHarjeet || 0)
      // Lab: const companyRate = Number(row.myShare || 0)
      const companyRate = Number(row.myShare || 0)
      if (companyRate <= 0 || rate <= 0) {
        // Lab returns early — still hydrate min so zeros become Lab values.
        rates[name] = syncMinStake(
          { ...row, minStake: resolveMinStake(row) },
          name,
          tm.currencyType,
        )
        continue
      }
      const isINR = tm.currencyType === 'INR'
      const targetBFHarjeet = 100 - oldCompanyHarjeet
      const targetAmountCut = isINR
        ? targetBFHarjeet * companyRate
        : (targetBFHarjeet / 100) * companyRate
      const percent = Math.round((targetAmountCut * 100) / rate)
      let next: VolumeRateCell = {
        ...row,
        myShare: companyRate,
        percent,
        companyHarjeet: oldCompanyHarjeet,
      }
      next = recalcFromMinStake(next, name, tm.currencyType, rate, 'percent')
      next.companyHarjeet = oldCompanyHarjeet
      rates[name] = next
    }
    return { ...tm, betfairRates: rates }
  })
}

/** Parse number input without turning intermediate "" into forced NaN display issues. */
export function parseNumInput(raw: string): number {
  if (raw.trim() === '') return 0
  const n = Number(raw)
  return Number.isFinite(n) ? n : 0
}

/** Display form for volume % fields — never pads with leading zeros. */
export function formatVolumeNumDisplay(n: number): string {
  if (!Number.isFinite(n)) return ''
  return String(n)
}

/** Lab NEW / CM Volume Percent — INR and POINTS both use plain % (100 = 100%). */
export function isInrVolumeCurrency(currencyType: unknown): boolean {
  return String(currencyType ?? '')
    .trim()
    .toUpperCase() === 'INR'
}

/**
 * Legacy CM-UI #86 stored INR place-% ×100 (5000 = 50%). Lab NEW is plain.
 * Values ≥1000 are almost always that legacy scale (sane Lab NEW sits under ~1000).
 */
export function normalizePlainVolumePercent(
  stored: number,
  currencyType: unknown,
): number {
  const n = Number(stored)
  if (!Number.isFinite(n) || n === 0) return 0
  if (isInrVolumeCurrency(currencyType) && n >= 1000) {
    const plain = n / 100
    return Number.isInteger(plain) ? plain : Number(plain.toFixed(4))
  }
  return n
}

/** @deprecated Lab NEW is 1:1 — kept as identity for any lingering callers. */
export function toDisplayVolumePercent(
  stored: number,
  _currencyType?: unknown,
): number {
  const n = Number(stored)
  if (!Number.isFinite(n) || n === 0) return 0
  return n
}

/** @deprecated Lab NEW is 1:1 — kept as identity for any lingering callers. */
export function fromDisplayVolumePercent(
  display: number,
  _currencyType?: unknown,
): number {
  const n = Number(display)
  if (!Number.isFinite(n) || n === 0) return 0
  return Math.round(n)
}

/**
 * Sanitize draft while typing. Returns `null` if the keystroke is invalid.
 * Strips leading zeros (`012` → `12`, `0100` → `100`) but keeps `0` and `0.x`.
 */
export function sanitizeVolumeNumDraft(raw: string): string | null {
  if (raw === '') return ''
  if (!/^\d*\.?\d*$/.test(raw)) return null
  if (raw === '.') return '.'
  if (raw.startsWith('0.') || raw === '0') return raw
  if (/^0+\d/.test(raw)) {
    const stripped = raw.replace(/^0+/, '')
    return stripped === '' ? '0' : stripped
  }
  if (/^0{2,}$/.test(raw)) return '0'
  return raw
}
